/**
 * GA4 ecommerce event names and payload builders.
 *
 * Why the standard names matter: GA4 only provides its built-in ecommerce reports and funnel
 * exploration for its own recommended event names. The events currently reaching GA4 through
 * GTM (`AddtoCart`, `check_out_complete___use_this_one`) are treated as unknown custom events,
 * which is why no product-level report or checkout funnel can be built today.
 *
 * Pure helpers only — sending stays with `useGA().logEventGA` so the layering holds.
 */

export const GA_EVENT = {
  VIEW_ITEM: 'view_item',
  ADD_TO_CART: 'add_to_cart',
  BEGIN_CHECKOUT: 'begin_checkout',
  ADD_PAYMENT_INFO: 'add_payment_info',
  PURCHASE: 'purchase',
  REMOVE_FROM_CART: 'remove_from_cart',
  // Not a GA4 recommended name — there isn't one for a failed payment. Kept in snake_case so
  // it sits alongside the standard events rather than looking like a stray legacy tag.
  PAYMENT_FAILED: 'payment_failed',
} as const

export const GA_CURRENCY = 'GBP'

export type GaItem = {
  item_id: string
  item_name?: string
  item_category?: string
  price?: number
  quantity?: number
}

/**
 * Coerce to a finite number, or undefined.
 *
 * The null/'' guard is load-bearing, not defensive noise: `Number(null)` and `Number('')` are
 * both 0, which is finite. Without it every `num(a) ?? num(b)` fallback below would stop at the
 * first null field and report a price of 0 — and `quantity_discount_price` is null on most real
 * cart lines, so that is the common case, not the edge case.
 */
export const num = (v: unknown): number | undefined => {
  if (v === null || v === undefined || v === '') return undefined
  const n = Number(v)
  return Number.isFinite(n) ? n : undefined
}

/**
 * Map a cart/order line to a GA4 item.
 *
 * Cart and order lines carry the same shape (both derive from the order_items table), but the
 * discounted price field is only present on cart lines — hence the fallback chain rather than a
 * single field. Anything unresolvable is left undefined: GA4 drops undefined keys, whereas a 0
 * or empty string would land in reports as real data.
 */
export function toGaItems(lines?: Array<Record<string, any>> | null): GaItem[] {
  if (!Array.isArray(lines)) return []
  return lines
    .filter(line => line?.competition_id != null)
    .map(line => {
      const item: GaItem = { item_id: String(line.competition_id) }
      if (line.title) item.item_name = String(line.title)
      const price = num(line.quantity_discount_price) ?? num(line.price) ?? num(line.normal_price)
      if (price !== undefined) item.price = price
      const qty = num(line.qty) ?? num(line.quantity)
      if (qty !== undefined) item.quantity = qty
      return item
    })
}

/** `purchase` — fired once on the order confirmation page. */
export function buildPurchaseParams(orderId: string | number, order?: Record<string, any> | null) {
  return {
    transaction_id: String(orderId),
    value: num(order?.grand_total) ?? 0,
    currency: GA_CURRENCY,
    coupon: order?.coupon_code || undefined,
    items: toGaItems(order?.items),
  }
}

/** `begin_checkout` — fired once when the cart page has loaded a non-empty cart. */
export function buildBeginCheckoutParams(cart?: Record<string, any> | null) {
  return {
    value: num(cart?.grand_total) ?? 0,
    currency: GA_CURRENCY,
    coupon: cart?.coupon_code || undefined,
    items: toGaItems(cart?.items),
  }
}

/** `add_payment_info` — fired when a payment method has been chosen and submitted. */
export function buildAddPaymentInfoParams(cart?: Record<string, any> | null, paymentType?: string) {
  return {
    value: num(cart?.grand_total) ?? 0,
    currency: GA_CURRENCY,
    coupon: cart?.coupon_code || undefined,
    payment_type: paymentType || undefined,
    items: toGaItems(cart?.items),
  }
}

/** `view_item` — fired on a competition detail page. */
export function buildViewItemParams(competition?: Record<string, any> | null) {
  if (!competition) return { currency: GA_CURRENCY, value: 0, items: [] }
  const price = num(competition.sale_price) ?? num(competition.price)
  const item: GaItem = { item_id: String(competition.id ?? competition.short_name ?? '') }
  if (competition.title) item.item_name = String(competition.title)
  if (price !== undefined) item.price = price
  item.quantity = 1
  return { currency: GA_CURRENCY, value: price ?? 0, items: [item] }
}

/**
 * `add_to_cart` — fired when tickets are added from a competition page.
 *
 * `value` is price x quantity, not unit price: GA4's cart-abandonment and funnel reports sum
 * this field, so sending the unit price would understate every multi-ticket basket.
 */
export function buildAddToCartParams(competition?: Record<string, any> | null, qty?: number) {
  const quantity = num(qty) ?? 1
  if (!competition) return { currency: GA_CURRENCY, value: 0, items: [] }
  const price = num(competition.quantity_discount_price) ?? num(competition.sale_price) ?? num(competition.price)
  const item: GaItem = { item_id: String(competition.id ?? competition.competition_id ?? '') }
  if (competition.title) item.item_name = String(competition.title)
  if (price !== undefined) item.price = price
  item.quantity = quantity
  return {
    currency: GA_CURRENCY,
    value: price !== undefined ? Number((price * quantity).toFixed(4)) : 0,
    items: [item],
  }
}

/**
 * `remove_from_cart`
 *
 * `passive` separates a user deleting a line from the system pulling one (competition closed,
 * hold expired). Folding those together would read as churn that never happened.
 */
export function buildRemoveFromCartParams(line?: Record<string, any> | null, passive = false) {
  const items = toGaItems(line ? [line] : [])
  const price = items[0]?.price ?? 0
  const qty = items[0]?.quantity ?? 0
  return {
    value: Number((price * qty).toFixed(4)),
    currency: GA_CURRENCY,
    removal_type: passive ? 'system' : 'user',
    items,
  }
}

/**
 * `payment_failed`
 *
 * Checkout failures currently leave no trace in any of the eight analytics tools on the site —
 * cancelled orders can only be counted after the fact in the database, with no way to segment by
 * gateway, amount or device, and no way to tell whether the customer retried.
 */
export function buildPaymentFailedParams(cart?: Record<string, any> | null, paymentType?: string, errorCode?: string | number) {
  return {
    value: num(cart?.grand_total) ?? 0,
    currency: GA_CURRENCY,
    payment_type: paymentType || undefined,
    // The backend's business code (e.g. 1015 = monthly spend limit), not an HTTP status.
    error_code: errorCode != null ? String(errorCode) : undefined,
    coupon: cart?.coupon_code || undefined,
    // A scalar, not `items`: GA4 only applies its ecommerce item encoding to its own recommended
    // event names, so an `items` array on a custom event is dropped in transit — verified by
    // capturing the outgoing request. The count still separates a failed single-ticket basket
    // from a failed twenty-line one, which is the part worth segmenting on.
    // Needs registering as a custom dimension in GA4 to appear in reports; it reaches BigQuery
    // either way.
    item_count: toGaItems(cart?.items).length || undefined,
  }
}
